import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  DRAWER_BREAKPOINT,
  NAVIGATION,
  PROFILE_MENU,
  MOCK_USER,
  MOCK_NOTIFICATIONS,
  ICON_PATHS,
  NOTIF_ICON_BY_TYPE,
  PAGE_TITLES,
} from "../Constants/DashboardLayout";
import "../Styles/DashboardLayoutStyle.css";

/* ============================================================================
   IHN BUSINESS DASHBOARD — APPLICATION SHELL
   Wraps existing pages via <Outlet />. All constants live in
   Constants/DashboardLayout.js and all styling in Styles/DashboardLayoutStyle.css.
   ========================================================================== */

/* ----------------------------------------------------------------------------
   ICONS — stroke-based SVG driven by ICON_PATHS from the constants file
   -------------------------------------------------------------------------- */

const Icon = ({ name, size = 18, strokeWidth = 1.8, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`Dashboard-icon ${className}`.trim()}
    aria-hidden="true"
  >
    <path d={ICON_PATHS[name] || ""} />
  </svg>
);

/* ----------------------------------------------------------------------------
   HELPER HOOKS
   -------------------------------------------------------------------------- */

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );
  useEffect(() => {
    let frame;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(window.innerWidth));
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(frame);
    };
  }, []);
  return width;
}

function useOnClickOutside(refs, handler, active) {
  useEffect(() => {
    if (!active) return;
    const listener = (e) => {
      const list = Array.isArray(refs) ? refs : [refs];
      const clickedInside = list.some((r) => r.current && r.current.contains(e.target));
      if (!clickedInside) handler(e);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [refs, handler, active]);
}

function useEscapeKey(handler, active) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && handler(e);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [handler, active]);
}

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ----------------------------------------------------------------------------
   SIDEBAR SUB-COMPONENTS
   -------------------------------------------------------------------------- */

const Logo = () => (
  <div className="Dashboard-logo-row">
    <div className="Dashboard-logo-mark">IHN</div>
    <div className="Dashboard-logo-text">
      <span className="Dashboard-logo-title">IHN</span>
      <span className="Dashboard-logo-subtitle">Business Community</span>
    </div>
  </div>
);

const NavItem = ({ item, onNavigate }) => (
  <NavLink
    to={item.path}
    end={item.end}
    onClick={onNavigate}
    className={({ isActive }) =>
      `Dashboard-nav-item${isActive ? " Dashboard-nav-item--active" : ""}`
    }
  >
    {({ isActive }) => (
      <>
        <Icon name={item.icon} size={18} />
        <span>{item.label}</span>
        {!!item.badge && (
          <span
            className={`Dashboard-nav-badge${
              isActive ? " Dashboard-nav-badge--active" : ""
            }`}
          >
            {item.badge}
          </span>
        )}
      </>
    )}
  </NavLink>
);

const SidebarSection = ({ section, onNavigate }) => (
  <div className="Dashboard-nav-section">
    <div className="Dashboard-nav-section-title">{section.section}</div>
    {section.items.map((item) => (
      <NavItem key={item.path} item={item} onNavigate={onNavigate} />
    ))}
  </div>
);

const Sidebar = ({ open, onClose, onMouseEnter, onMouseLeave }) => (
  <aside
    className={`Dashboard-sidebar${open ? "" : " Dashboard-sidebar--closed"}`}
    aria-label="Sidebar navigation"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    <div className="Dashboard-sidebar-headrow">
      <Logo />
      <button
        type="button"
        onClick={onClose}
        aria-label="Close sidebar"
        className="Dashboard-sidebar-close"
      >
        <Icon name="close" size={18} />
      </button>
    </div>
    <nav className="Dashboard-nav-scroll" aria-label="Primary navigation">
      {NAVIGATION.map((section) => (
        <SidebarSection key={section.section} section={section} />
      ))}
    </nav>
    <div className="Dashboard-sidebar-footer">&copy; {new Date().getFullYear()} IHN Business Network</div>
  </aside>
);

const MobileDrawer = ({ open, onClose }) => {
  useEscapeKey(onClose, open);

  return (
    <>
      {open && (
        <div
          className="Dashboard-drawer-overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        className={`Dashboard-mobile-drawer${open ? " Dashboard-mobile-drawer--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="Dashboard-drawer-top">
          <Logo />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation menu"
            className="Dashboard-header-btn Dashboard-drawer-close Dashboard-hamburger"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <nav className="Dashboard-nav-scroll" aria-label="Primary navigation">
          {NAVIGATION.map((section) => (
            <SidebarSection key={section.section} section={section} onNavigate={onClose} />
          ))}
        </nav>
        <div className="Dashboard-sidebar-footer">&copy; {new Date().getFullYear()} IHN Business Network</div>
      </div>
    </>
  );
};

/* ----------------------------------------------------------------------------
   NOTIFICATION PANEL
   -------------------------------------------------------------------------- */

const NotificationBell = ({ notifications, onToggle, isOpen, btnRef }) => {
  const unreadCount = notifications.filter((n) => !n.read).length;
  return (
    <button
      ref={btnRef}
      type="button"
      onClick={onToggle}
      className="Dashboard-header-btn"
      aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
      aria-haspopup="true"
      aria-expanded={isOpen}
    >
      <Icon name="bell" size={20} />
      {unreadCount > 0 && (
        <span className="Dashboard-bell-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
      )}
    </button>
  );
};

const NotificationPanel = ({ notifications, onMarkAllRead, onClose, panelRef }) => (
  <div
    ref={panelRef}
    className="Dashboard-panel Dashboard-fade-in"
    role="menu"
    aria-label="Notifications"
  >
    <div className="Dashboard-panel-header">
      <span className="Dashboard-panel-title">Notifications</span>
      <button type="button" onClick={onMarkAllRead} className="Dashboard-mark-read">
        Mark all as read
      </button>
    </div>
    <div className="Dashboard-notif-scroll">
      {notifications.map((n) => (
        <div
          key={n.id}
          className={`Dashboard-notif-item${n.read ? "" : " Dashboard-notif-item--unread"}`}
          role="menuitem"
          tabIndex={0}
        >
          <span className={`Dashboard-notif-dot${n.read ? "" : " Dashboard-notif-dot--unread"}`} />
          <div className="Dashboard-notif-inner">
            <div className="Dashboard-menu-icon Dashboard-menu-icon--compact">
              <Icon name={NOTIF_ICON_BY_TYPE[n.type] || "bell"} size={15} />
            </div>
            <div className="Dashboard-notif-body">
              <div className="Dashboard-notif-title">{n.title}</div>
              <div className="Dashboard-notif-desc">{n.desc}</div>
              <div className="Dashboard-notif-time">{n.time}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
    <div
      className="Dashboard-panel-footer"
      role="menuitem"
      tabIndex={0}
      onClick={onClose}
    >
      View All Notifications
    </div>
  </div>
);

/* ----------------------------------------------------------------------------
   PROFILE DROPDOWN
   -------------------------------------------------------------------------- */

const ProfileDropdown = ({ user, onNavigate, onLogout, panelRef }) => (
  <div ref={panelRef} className="Dashboard-panel Dashboard-fade-in" role="menu" aria-label="Profile menu">
    <div className="Dashboard-profile-header">
      <div className="Dashboard-avatar Dashboard-avatar--lg">{user.initials}</div>
      <div>
        <div className="Dashboard-profile-name">{user.name}</div>
        <div className="Dashboard-profile-role">{user.designation}</div>
      </div>
    </div>
    <div className="Dashboard-menu-list">
      {PROFILE_MENU.map((item) => (
        <div
          key={item.label}
          className="Dashboard-menu-item"
          role="menuitem"
          tabIndex={0}
          onClick={() => onNavigate(item.path)}
          onKeyDown={(e) => e.key === "Enter" && onNavigate(item.path)}
        >
          <span className="Dashboard-menu-icon">
            <Icon name={item.icon} size={16} />
          </span>
          <div>
            <div className="Dashboard-menu-label">{item.label}</div>
            <div className="Dashboard-menu-desc">{item.desc}</div>
          </div>
        </div>
      ))}
    </div>
    <div
      className="Dashboard-logout-row"
      role="menuitem"
      tabIndex={0}
      onClick={onLogout}
      onKeyDown={(e) => e.key === "Enter" && onLogout()}
    >
      <Icon name="logout" size={17} />
      <span>Logout</span>
    </div>
  </div>
);

/* ----------------------------------------------------------------------------
   HEADER
   -------------------------------------------------------------------------- */

const Header = ({ user, notifications, onMarkAllRead, onOpenDrawer, onLogout, showSidebarHamburger = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const notifBtnRef = useRef(null);
  const notifPanelRef = useRef(null);
  const profileBtnRef = useRef(null);
  const profilePanelRef = useRef(null);

  useOnClickOutside([notifBtnRef, notifPanelRef], () => setNotifOpen(false), notifOpen);
  useOnClickOutside([profileBtnRef, profilePanelRef], () => setProfileOpen(false), profileOpen);
  useEscapeKey(() => {
    setNotifOpen(false);
    setProfileOpen(false);
  }, notifOpen || profileOpen);

  const pageTitle = PAGE_TITLES[location.pathname] || "Dashboard";

  const handleProfileNavigate = useCallback(
    (path) => {
      setProfileOpen(false);
      navigate(path);
    },
    [navigate]
  );

  return (
    <header className="Dashboard-header">
      <div className="Dashboard-header-left">
        <button
          type="button"
          className={`Dashboard-header-btn Dashboard-hamburger${
            showSidebarHamburger ? " Dashboard-hamburger--always" : ""
          }`}
          onClick={onOpenDrawer}
          aria-label="Open navigation menu"
        >
          <Icon name="menu" size={22} />
        </button>
        <span className="Dashboard-breadcrumb-title">{pageTitle}</span>
      </div>

      <div className="Dashboard-header-right">
        <div className="Dashboard-header-slot">
          <NotificationBell
            notifications={notifications}
            isOpen={notifOpen}
            btnRef={notifBtnRef}
            onToggle={() => {
              setNotifOpen((v) => !v);
              setProfileOpen(false);
            }}
          />
          {notifOpen && (
            <NotificationPanel
              panelRef={notifPanelRef}
              notifications={notifications}
              onMarkAllRead={onMarkAllRead}
              onClose={() => setNotifOpen(false)}
            />
          )}
        </div>

        <div className="Dashboard-header-slot">
          <button
            ref={profileBtnRef}
            type="button"
            className="Dashboard-profile-btn"
            onClick={() => {
              setProfileOpen((v) => !v);
              setNotifOpen(false);
            }}
            aria-label="Open profile menu"
            aria-haspopup="true"
            aria-expanded={profileOpen}
          >
            <div className="Dashboard-avatar">{user.initials}</div>
            <div className="Dashboard-profile-text">
              <span className="Dashboard-profile-name">{user.name}</span>
              <span className="Dashboard-profile-role">{user.designation}</span>
            </div>
            <Icon
              name="chevronDown"
              size={16}
              className={profileOpen ? "Dashboard-chevron Dashboard-chevron--open" : "Dashboard-chevron"}
            />
          </button>
          {profileOpen && (
            <ProfileDropdown
              panelRef={profilePanelRef}
              user={user}
              onNavigate={handleProfileNavigate}
              onLogout={onLogout}
            />
          )}
        </div>
      </div>
    </header>
  );
};

/* ----------------------------------------------------------------------------
   DASHBOARD LAYOUT (default export)
   -------------------------------------------------------------------------- */

const DashboardLayout = ({ user = MOCK_USER, children, onLogout }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const width = useViewportWidth();
  const location = useLocation();

  /* Close the mobile drawer whenever the route changes or viewport grows past the breakpoint */
  useEffect(() => setDrawerOpen(false), [location.pathname]);
  useEffect(() => {
    if (width >= DRAWER_BREAKPOINT) setDrawerOpen(false);
  }, [width]);

  /* Auto-close the desktop sidebar 5s after the mouse leaves it */
  const sidebarTimerRef = useRef(null);

  const clearSidebarTimer = useCallback(() => {
    if (sidebarTimerRef.current) {
      clearTimeout(sidebarTimerRef.current);
      sidebarTimerRef.current = null;
    }
  }, []);

  const handleSidebarEnter = useCallback(() => {
    clearSidebarTimer();
  }, [clearSidebarTimer]);

  const handleSidebarLeave = useCallback(() => {
    if (width < DRAWER_BREAKPOINT) return;
    clearSidebarTimer();
    sidebarTimerRef.current = setTimeout(() => {
      setSidebarOpen(false);
      sidebarTimerRef.current = null;
    }, 5000);
  }, [width, clearSidebarTimer]);

  useEffect(() => () => clearSidebarTimer(), [clearSidebarTimer]);
  useEffect(() => {
    if (!sidebarOpen) clearSidebarTimer();
  }, [sidebarOpen, clearSidebarTimer]);

  /* Lock body scroll while the mobile drawer is open */
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const handleMarkAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const handleLogout = useCallback(() => {
    if (typeof onLogout === "function") {
      onLogout();
    } else {
      // eslint-disable-next-line no-console
      console.log("Logout requested");
    }
  }, [onLogout]);

  const resolvedUser = useMemo(
    () => ({ ...MOCK_USER, ...user, initials: user?.initials || getInitials(user?.name || MOCK_USER.name) }),
    [user]
  );

  return (
    <div className={sidebarOpen ? "Dashboard-layout" : "Dashboard-layout Dashboard-layout--sidebar-collapsed"}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onMouseEnter={handleSidebarEnter}
        onMouseLeave={handleSidebarLeave}
      />
      {!sidebarOpen && (
        <span className="Dashboard-sidebar-reopen">
          <button
            type="button"
            className="Dashboard-sidebar-reopen-badge"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
            title="Open sidebar"
          >
            <span aria-hidden="true">❯</span>
          </button>
        </span>
      )}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="Dashboard-main">
        <Header
          user={resolvedUser}
          notifications={notifications}
          onMarkAllRead={handleMarkAllRead}
          showSidebarHamburger={!sidebarOpen}
          onOpenDrawer={() => {
            if (width >= DRAWER_BREAKPOINT) {
              setSidebarOpen(true);
            } else {
              setDrawerOpen(true);
            }
          }}
          onLogout={handleLogout}
        />
        <main className="Dashboard-content">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
